import { useEffect, useState } from 'react';
import { inSalishSea, parseCoordinates, type GeocodeResult } from '@whaletail/shared';
import { ApiError, geocodeAddress } from '../api.ts';
import type { MyLocation } from '../geolocation.ts';

export interface PickedLocation {
  lat: number;
  lon: number;
  /** The address that produced this point, when it came from a search. */
  label?: string | null;
}

type Method = 'address' | 'coords' | 'map';

interface Props {
  value: PickedLocation | null;
  onChange: (v: PickedLocation | null) => void;
  /** True while the map is waiting for a click. */
  picking: boolean;
  onPickingChange: (active: boolean) => void;
  myLocation: MyLocation | null;
  /** Turns "you are here" on if it is off, so the button always does something. */
  onWantMyLocation: () => void;
  onFlyTo: (lat: number, lon: number, zoom?: number) => void;
  /** Restrict to the Salish Sea (view points). Home may be anywhere. */
  requireRegion: boolean;
}

/**
 * Choose a place three ways: type an address, enter GPS coordinates, or click
 * the map. Whichever is used, the result is one draggable pin on the map that
 * can be nudged afterwards, so a rough address hit is easy to correct.
 */
export function LocationPicker({
  value,
  onChange,
  picking,
  onPickingChange,
  myLocation,
  onWantMyLocation,
  onFlyTo,
  requireRegion,
}: Props) {
  const [method, setMethod] = useState<Method>('address');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GeocodeResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [coordText, setCoordText] = useState('');
  const [coordError, setCoordError] = useState<string | null>(null);
  const [awaitingHere, setAwaitingHere] = useState(false);

  // `picking` can be turned on from outside (the map's "Add place" button
  // arms it directly), not just by choosing the "Click map" tab here — keep
  // the visible tab in sync either way, so the pane shown always matches
  // what clicking the map will actually do.
  useEffect(() => {
    if (picking) setMethod('map');
  }, [picking]);

  const choose = (m: Method) => {
    setMethod(m);
    onPickingChange(m === 'map');
  };

  const place = (p: PickedLocation, zoom = 15) => {
    onChange(p);
    onFlyTo(p.lat, p.lon, zoom);
  };

  const search = async () => {
    const q = query.trim();
    if (q.length < 3) return setAddressError('Type at least 3 characters of an address or place.');
    // Someone may paste coordinates into the address box; do the obvious thing.
    const asCoords = parseCoordinates(q);
    if (asCoords) {
      setCoordText(q);
      return applyCoords(q);
    }
    setSearching(true);
    setAddressError(null);
    setResults(null);
    try {
      const found = await geocodeAddress(q);
      setResults(found);
      if (found.length === 0) setAddressError('No match in the Salish Sea area. Try adding the city, or use coordinates.');
    } catch (err) {
      setAddressError(err instanceof ApiError ? err.message : 'Address search failed.');
    } finally {
      setSearching(false);
    }
  };

  const applyCoords = (text: string) => {
    const c = parseCoordinates(text);
    if (!c) {
      setCoordError('Not recognised. Try 47.6062, -122.3321 or 47°36.372′N 122°19.926′W.');
      return;
    }
    if (requireRegion && !inSalishSea(c.lat, c.lon)) {
      setCoordError('That point is outside the Salish Sea area this map covers.');
      return;
    }
    setCoordError(null);
    place({ lat: c.lat, lon: c.lon, label: null });
  };

  // If location was off when "Use my current location" was pressed, turn it on
  // and apply the first fix as soon as it arrives.
  useEffect(() => {
    if (awaitingHere && myLocation) {
      setAwaitingHere(false);
      place({ lat: myLocation.lat, lon: myLocation.lon, label: null }, 16);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingHere, myLocation]);

  const useMyLocation = () => {
    setAddressError(null);
    if (!myLocation) {
      setAwaitingHere(true);
      onWantMyLocation();
      return;
    }
    place({ lat: myLocation.lat, lon: myLocation.lon, label: null }, 16);
  };

  const outside = value && requireRegion && !inSalishSea(value.lat, value.lon);

  return (
    <div className="picker">
      <div className="seg" role="tablist" aria-label="How to choose the location">
        {(
          [
            ['address', 'Address'],
            ['coords', 'GPS'],
            ['map', 'Click map'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={method === id}
            className="seg-btn"
            onClick={() => choose(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {method === 'address' && (
        // A plain div, not a <form>: this picker is itself used inside another
        // form (submitting a view point), and a nested <form> is invalid HTML —
        // browsers handle its submit event inconsistently, which was letting
        // "Find" trigger the outer form's submit (or a real page navigation)
        // instead of just running the search.
        <div className="picker-pane">
          <div className="row">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void search();
                }
              }}
              placeholder="Street address or place name"
              aria-label="Street address or place name"
              maxLength={200}
            />
            <button type="button" className="btn" disabled={searching} onClick={() => void search()}>
              {searching ? '…' : 'Find'}
            </button>
          </div>
          {addressError && <p className="form-error">{addressError}</p>}
          {results && results.length > 0 && (
            <ul className="result-list">
              {results.map((r, i) => (
                <li key={`${r.lat},${r.lon},${i}`}>
                  <button type="button" className="result" onClick={() => place({ lat: r.lat, lon: r.lon, label: r.label })}>
                    {r.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <small className="hint">Addresses are looked up through OpenStreetMap.</small>
        </div>
      )}

      {method === 'coords' && (
        <div className="picker-pane">
          <div className="row">
            <input
              value={coordText}
              onChange={(e) => {
                setCoordText(e.target.value);
                setCoordError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyCoords(coordText);
                }
              }}
              placeholder="47.6062, -122.3321"
              aria-label="GPS coordinates"
              spellCheck={false}
              maxLength={80}
            />
            <button type="button" className="btn" onClick={() => applyCoords(coordText)}>
              Place
            </button>
          </div>
          {coordError && <p className="form-error">{coordError}</p>}
          <small className="hint">
            Decimal degrees, or degrees and minutes. Paste straight from Google Maps or a GPS.
          </small>
        </div>
      )}

      {method === 'map' && (
        <div className="picker-pane">
          <p className="hint">
            {picking ? 'Click the map to drop the pin. ' : 'Ready. '}
            You can drag the pin afterwards to fine-tune it.
          </p>
          {!picking && (
            <button type="button" className="btn" onClick={() => onPickingChange(true)}>
              Pick on Map
            </button>
          )}
        </div>
      )}

      <button type="button" className="link-btn here-btn" onClick={useMyLocation}>
        Use My Current Location
      </button>
      {awaitingHere && <small className="hint">Waiting for your device to report where you are…</small>}

      {value && (
        <div className="picked">
          <div>
            <strong>{value.lat.toFixed(5)}, {value.lon.toFixed(5)}</strong>
            {value.label && <div className="picked-label">{value.label}</div>}
          </div>
          <button type="button" className="link-btn" onClick={() => onChange(null)}>
            Clear
          </button>
        </div>
      )}
      {outside && <p className="form-error">That point is outside the Salish Sea area this map covers.</p>}
    </div>
  );
}
